// src/pages/maya-chat-simulator/threadMerge.ts
//
// How the simulator folds the polled WhatsApp thread into the bubbles it already
// shows. Kept free of imports so it can be checked on its own
// (scripts/check-sim-thread-merge.ts).

/** One message in the simulated thread (candidate = admin's typed message). */
export interface SimMessage {
    id: string;
    sender: "candidate" | "maya";
    body: string;
    at: Date;
    /** Present when this message is a file attachment (#3113) — renders as a file bubble. */
    file?: { name: string };
    /** An image the reply carried, shown above its caption the way WhatsApp does —
     *  the receptionist's service pictures (#4851). */
    imageUrl?: string;
}

/** A thread row, read down to what the merge needs. */
export interface ThreadRow {
    direction: "outbound" | "inbound";
    body: string;
    created_at: string;
    /** The row's first image, already picked out of its media. */
    imageUrl?: string;
}

/** Where an image lives, without the presign query: the same S3 picture is signed afresh
 *  on every read, so the reply and the thread carry different query strings (#4864). */
export function imagePath(url: string): string {
    try {
        const u = new URL(url);
        return `${u.origin}${u.pathname}`;
    } catch {
        return url.split("?")[0];
    }
}

/** What makes two bubbles the same message: sender and words, and the picture when it has one. */
export function bubbleKey(sender: SimMessage["sender"], body: string, imageUrl?: string): string {
    const base = `${sender}|${body.trim()}`;
    return imageUrl ? `${base}|img:${imagePath(imageUrl)}` : base;
}

/**
 * The bubbles after a poll: rows already shown (by sender, words and picture) are
 * skipped, a picture whose caption already shows as a text-only bubble lends that
 * bubble its picture (#4851), and everything else is added in time order.
 *
 * Only a bubble with no picture can take one by caption: two different pictures with
 * the same caption are two messages (Jack, #4865), and caption-less pictures (#4864)
 * are told apart by their image.
 */
export function mergeThreadRows(prev: SimMessage[], rows: ThreadRow[]): SimMessage[] {
    const index = new Map<string, number>();
    const textOnly = new Map<string, number>();
    prev.forEach((m, i) => {
        index.set(bubbleKey(m.sender, m.body, m.imageUrl), i);
        if (m.body.trim() && !m.imageUrl) textOnly.set(bubbleKey(m.sender, m.body), i);
    });
    let next = prev;
    const additions: SimMessage[] = [];
    for (const row of rows) {
        const sender: SimMessage["sender"] = row.direction === "outbound" ? "maya" : "candidate";
        const body = (row.body || "").trim();
        const imageUrl = row.imageUrl;
        if (!body && !imageUrl) continue;
        const key = bubbleKey(sender, body, imageUrl);
        if (index.has(key)) continue;
        const textKey = bubbleKey(sender, body);
        const textAt = body && imageUrl ? textOnly.get(textKey) : undefined;
        if (textAt !== undefined) {
            if (next === prev) next = [...prev];
            next[textAt] = { ...next[textAt], imageUrl };
            index.set(key, textAt);
            textOnly.delete(textKey);
            continue;
        }
        index.set(key, -1);
        additions.push({
            id: `thread:${row.created_at}:${sender}:${additions.length}`,
            sender,
            body: row.body,
            at: new Date(row.created_at),
            ...(imageUrl ? { imageUrl } : {}),
        });
    }
    if (additions.length === 0) return next;
    return [...next, ...additions].sort((a, b) => a.at.getTime() - b.at.getTime());
}
